import React, { useEffect, useId } from 'react';
import { useScopePolicy, type BannerSurfaceId } from '@frontend/scope-access';
import { useGlobalBannerStore } from '../store';
import type { BannerType, BannerAction } from '../types';

export interface GlobalBannerDynamicProps {
  id?: string;
  type: BannerType;
  priority?: number;
  isDismissible?: boolean;
  onDismiss?: () => void;
  title: string;
  message: React.ReactNode;
  action?: BannerAction;
  trackingId?: string;
  dismissBtnTrackingId?: string;
  'data-testid'?: string;
  onVisible?: () => void;
  /**
   * Identifies this banner for workspace scope access control.
   *
   * When provided, the banner is checked against BANNER_PARENTS in the
   * scope-access-engine registry. In workspace scope, only banners whose
   * parent capability is allowed will render.
   *
   * Banners without a surfaceId are hidden by default in workspace scope
   * (deny-by-default). In non-workspace scopes, all banners render regardless.
   */
  surfaceId?: BannerSurfaceId;
}

export const GlobalBannerDynamic: React.FC<GlobalBannerDynamicProps> = ({
  id: providedId,
  type,
  priority = 1,
  isDismissible = true,
  onDismiss,
  title,
  message,
  action,
  trackingId,
  dismissBtnTrackingId,
  'data-testid': dataTestId,
  onVisible,
  surfaceId,
}) => {
  const generatedId = useId();
  const id = providedId || generatedId;
  const { registerBanner, unregisterBanner } = useGlobalBannerStore(['registerBanner', 'unregisterBanner']);
  const { canShowBanner } = useScopePolicy();
  const isAllowed = canShowBanner(surfaceId);

  useEffect(() => {
    if (!isAllowed) return;

    registerBanner({
      id,
      type,
      priority,
      title,
      message,
      onDismiss,
      action,
      isDismissible,
      trackingId,
      dismissBtnTrackingId,
      'data-testid': dataTestId,
      onVisible,
    });

    return () => unregisterBanner(id);
  }, [
    isAllowed,
    id,
    type,
    priority,
    isDismissible,
    onDismiss,
    title,
    message,
    action,
    trackingId,
    dismissBtnTrackingId,
    dataTestId,
    onVisible,
  ]);

  return null;
};
